import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { MOTION, r } from './landingTokens';
import { recentScrollJump, meaningfullyInViewport } from './scrollJump';

// ─── Scroll-triggered entrance ────────────────────────────────────────────────
// Fades + rises its children when they scroll into view. Honors
// prefers-reduced-motion (renders instantly, no transform). `delay` staggers
// siblings; `draw` grows a rule from the left (scaleX) for the section accent.
//
// Scroll dynamics (harmony rules):
// - Reveals fire once the element rises past MOTION.trigger (~25% up the
//   viewport), so motion plays in the reading zone.
// - Hysteresis: with `repeat`, the element only resets once it is FULLY
//   off-screen — nothing ever visibly un-reveals in the lower viewport.
// - Direction-aware: entering from ABOVE (scrolling back up) shows instantly,
//   with no transition — entrances only play in the reading direction (down).
const prefersReduced = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function Reveal({ children, delay = 0, y = MOTION.y, x = 0, duration = MOTION.duration, draw = false, repeat = false, rootMargin = MOTION.trigger, sx, ...rest }) {
  const ref = useRef(null);
  const [state, setState] = useState({ shown: false, instant: true });
  // Which side of the viewport the element last left from. Sampling position at
  // the trigger callback is unreliable — a fast wheel step can jump a small
  // element clear across the boundary — so we remember the exit side instead.
  const lastOffSide = useRef('below');

  useEffect(() => {
    if (prefersReduced()) { setState({ shown: true, instant: true }); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    // Already on screen at mount (remount into a scrolled page, back-navigation
    // before the scroll restore lands): show instantly. Entrances are for
    // content the reader scrolls to, never for content that's simply there.
    if (meaningfullyInViewport(el)) setState({ shown: true, instant: true });
    // Trigger: fires when the element crosses the reading-zone line. Re-entering
    // from above (scroll-up) or right after a scroll jump (back-navigation
    // restore, anchor jump) shows instantly; from below (reading direction)
    // animates. `top <= 0` reads the entry side from this delivery's own
    // geometry — entering from above always straddles the viewport top — so
    // the from-above case never depends on ioReset callback ordering.
    const ioTrigger = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const fromAbove = e.boundingClientRect.top <= 0 || lastOffSide.current === 'above';
            setState({ shown: true, instant: fromAbove || recentScrollJump() });
            if (!repeat) ioTrigger.disconnect();
          }
        });
      },
      { threshold: 0, rootMargin }
    );
    ioTrigger.observe(el);
    // Reset: a separate FULL-viewport observer — the trigger observer stops
    // firing once the element has left its shrunken root, so it can never see
    // the "fully off-screen" transition. Reset is instant (it's invisible).
    // Its positive edge doubles as the jump catcher for the bottom quarter of
    // the viewport: after a jump, elements landing below the trigger line
    // would otherwise stay invisible until the next scroll.
    let ioReset;
    if (repeat) {
      ioReset = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) {
              lastOffSide.current = e.boundingClientRect.bottom <= 0 ? 'above' : 'below';
              setState({ shown: false, instant: true });
            } else {
              // Re-learn the side on ENTRY too — a jump can carry the element
              // from one off-screen side to the other with no intersection in
              // between, which would leave the exit-side record stale and make
              // the next reading entry wrongly instant.
              lastOffSide.current = e.boundingClientRect.top <= 0 ? 'above' : 'below';
              if (recentScrollJump() && meaningfullyInViewport(e.boundingClientRect)) setState((s) => (s.shown ? s : { shown: true, instant: true }));
            }
          });
        },
        { threshold: 0 }
      );
      ioReset.observe(el);
    }
    return () => { ioTrigger.disconnect(); if (ioReset) ioReset.disconnect(); };
  }, [repeat, rootMargin]);

  const { shown, instant } = state;
  return (
    <Box
      ref={ref}
      sx={{
        opacity: draw ? 1 : shown ? 1 : 0,
        transformOrigin: draw ? 'left center' : undefined,
        transform: shown ? 'none' : draw ? 'scaleX(0)' : `translate(${r(x)}, ${r(y)})`,
        transition: instant
          ? 'none'
          : `opacity ${duration}ms ${MOTION.ease} ${delay}ms, transform ${duration}ms ${MOTION.ease} ${delay}ms`,
        willChange: 'opacity, transform',
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}
